import * as React from "react";
import { Frown, Meh, Smile, Star, ThumbsDown, ThumbsUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReviewType } from "@/services/bedrock";
import type { CoachNpsCategoryFilter, CoachRecommendationFilter } from "./useCoachReviewFilters";

export function ratingValueOptions(ratingType: ReviewType | null): number[] {
  if (ratingType === "star") return [1, 2, 3, 4, 5];
  if (ratingType === "nps") return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  if (ratingType === "ten_point") return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return [];
}

const NPS_BANDS: Array<{
  id: CoachNpsCategoryFilter;
  label: string;
  scores: number[];
  Icon: typeof Smile;
  scoreClass: string;
  scoreSelectedClass: string;
  labelClass: string;
  labelSelectedClass: string;
}> = [
  {
    id: "detractor",
    label: "Detractors",
    scores: [0, 1, 2, 3, 4, 5, 6],
    Icon: Frown,
    scoreClass: "bg-rose-100 text-rose-700 hover:bg-rose-200",
    scoreSelectedClass: "bg-rose-500 text-white hover:bg-rose-500",
    labelClass: "text-rose-600 hover:bg-rose-50",
    labelSelectedClass: "text-rose-700 bg-rose-50 ring-1 ring-rose-200",
  },
  {
    id: "passive",
    label: "Passives",
    scores: [7, 8],
    Icon: Meh,
    scoreClass: "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
    scoreSelectedClass: "bg-yellow-500 text-white hover:bg-yellow-500",
    labelClass: "text-yellow-700 hover:bg-yellow-50",
    labelSelectedClass: "text-yellow-800 bg-yellow-50 ring-1 ring-yellow-200",
  },
  {
    id: "promoter",
    label: "Promoters",
    scores: [9, 10],
    Icon: Smile,
    scoreClass: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
    scoreSelectedClass: "bg-emerald-500 text-white hover:bg-emerald-500",
    labelClass: "text-emerald-700 hover:bg-emerald-50",
    labelSelectedClass: "text-emerald-800 bg-emerald-50 ring-1 ring-emerald-200",
  },
];

type ReviewRatingValueFilterProps = {
  ratingType: ReviewType;
  value: number | null;
  onChange: (value: number | null) => void;
  npsCategory?: CoachNpsCategoryFilter | null;
  onNpsCategoryChange?: (category: CoachNpsCategoryFilter | null) => void;
  recommendation?: CoachRecommendationFilter | null;
  onRecommendationChange?: (value: CoachRecommendationFilter | null) => void;
};

export function ReviewRatingValueFilter({
  ratingType,
  value,
  onChange,
  npsCategory = null,
  onNpsCategoryChange,
  recommendation = null,
  onRecommendationChange,
}: ReviewRatingValueFilterProps) {
  const [hoveredValue, setHoveredValue] = React.useState<number | null>(null);
  const values = ratingValueOptions(ratingType);

  if (ratingType === "text") {
    return (
      <div className="grid grid-cols-2 gap-1 w-full" role="group" aria-label="Recommendation">
        <button
          type="button"
          aria-pressed={recommendation === "positive"}
          title="Recommend"
          className={cn(
            "flex flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1.5 transition-colors cursor-pointer",
            recommendation === "positive"
              ? "text-emerald-800 bg-emerald-50 ring-1 ring-emerald-200"
              : "text-emerald-700 hover:bg-emerald-50"
          )}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onRecommendationChange?.(recommendation === "positive" ? null : "positive");
          }}
        >
          <ThumbsUp className="size-3.5 pointer-events-none shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-wide leading-none pointer-events-none">
            Recommend
          </span>
        </button>
        <button
          type="button"
          aria-pressed={recommendation === "negative"}
          title="Do not recommend"
          className={cn(
            "flex flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1.5 transition-colors cursor-pointer",
            recommendation === "negative"
              ? "text-rose-700 bg-rose-50 ring-1 ring-rose-200"
              : "text-rose-600 hover:bg-rose-50"
          )}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onRecommendationChange?.(recommendation === "negative" ? null : "negative");
          }}
        >
          <ThumbsDown className="size-3.5 pointer-events-none shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-wide leading-none pointer-events-none text-center">
            Do not recommend
          </span>
        </button>
      </div>
    );
  }

  if (values.length === 0) return null;

  if (ratingType === "star") {
    const previewValue = hoveredValue ?? value;
    return (
      <div
        className="flex items-center justify-center gap-0.5"
        role="radiogroup"
        aria-label="Star rating"
        onMouseLeave={() => setHoveredValue(null)}
      >
        {values.map(star => {
          const filled = previewValue != null && star <= previewValue;
          return (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={value === star}
              aria-label={`${star} star${star === 1 ? "" : "s"}`}
              className={cn(
                "p-1 rounded-md transition-colors cursor-pointer",
                filled ? "text-amber-400" : "text-slate-300"
              )}
              onMouseEnter={() => setHoveredValue(star)}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onChange(value === star ? null : star);
              }}
            >
              <Star className={cn("size-5 pointer-events-none", filled && "fill-amber-400")} strokeWidth={1.5} />
            </button>
          );
        })}
      </div>
    );
  }

  if (ratingType === "nps") {
    return (
      <div className="flex flex-col gap-2 w-full" role="group" aria-label="NPS score">
        <div className="flex gap-1 w-full">
          {NPS_BANDS.map(band => (
            <div key={band.id} className="flex gap-0.5 min-w-0" style={{ flex: band.scores.length }}>
              {band.scores.map(score => {
                const selected = value === score || npsCategory === band.id;
                return (
                  <button
                    key={score}
                    type="button"
                    aria-pressed={selected}
                    aria-label={`NPS ${score}`}
                    className={cn(
                      "flex-1 min-w-0 h-8 rounded-md text-xs font-bold transition-colors cursor-pointer",
                      selected ? band.scoreSelectedClass : band.scoreClass
                    )}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(value === score ? null : score);
                    }}
                  >
                    {score}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-1 w-full">
          {NPS_BANDS.map(band => {
            const categorySelected = npsCategory === band.id;
            const Icon = band.Icon;
            return (
              <button
                key={band.id}
                type="button"
                aria-pressed={categorySelected}
                title={band.label}
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1.5 transition-colors cursor-pointer",
                  categorySelected ? band.labelSelectedClass : band.labelClass
                )}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onNpsCategoryChange?.(categorySelected ? null : band.id);
                }}
              >
                <Icon className="size-3.5 pointer-events-none shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-wide leading-none pointer-events-none">
                  {band.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ten_point — continuous 1–10 scale with fill-up preview
  const previewValue = hoveredValue ?? value;
  return (
    <div
      className="flex flex-col gap-1.5 w-full"
      role="radiogroup"
      aria-label="10-point rating"
      onMouseLeave={() => setHoveredValue(null)}
    >
      <div className="flex w-full rounded-lg bg-slate-100/90 p-0.5 gap-px">
        {values.map(score => {
          const filled = previewValue != null && score <= previewValue;
          const selected = value === score;
          return (
            <button
              key={score}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${score} out of 10`}
              className={cn(
                "flex-1 min-w-0 h-8 text-xs font-semibold tabular-nums transition-all cursor-pointer first:rounded-l-md last:rounded-r-md",
                filled
                  ? selected
                    ? "bg-indigo-500 text-white shadow-sm"
                    : "bg-indigo-500/80 text-white"
                  : "text-slate-400 hover:text-slate-600"
              )}
              onMouseEnter={() => setHoveredValue(score)}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onChange(value === score ? null : score);
              }}
            >
              {score}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between px-1 text-[10px] font-medium text-slate-400">
        <span>Low</span>
        <span>High</span>
      </div>
    </div>
  );
}
